import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Swal from "sweetalert2";
import debounce from "lodash.debounce";
import { format } from "date-fns";
import GenericTableComponent from "../common/Table";
import { useModal } from "../../shared/hooks/useModal";
import Modal from "../Modal/Modal";
import styled from "styled-components";
import {
  getAllCampaigns,
  approveRejectCampaign,
  statusCampaign,
  getCpaByCampaignId,
  updateCampaignCpa,
  createPostbackSettings,
  getPostbackSettings,
} from "../../redux/slices/campaignSlice";
//import { convertToLocalCurrency } from '../../utils/currency';

const columns = [
  { id: "campaignName", label: "Campaign Name" },
  { id: "user", label: "Advertiser name" },
  { id: "createdAt", label: "Date created" },
  // { id: "flow", label: "Flow" },
  // { id: "isActive", label: "Status" },
  { id: "isApproved", label: "Approval" },
  { id: "campaignBudget", label: "Budget" },
];

const AllCampaign = () => {
  const dispatch = useDispatch();
  const { campaigns, meta, postbackSetting, loading } = useSelector(
    (state) => state.campaign
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const { showModal, hideModal, isModalOpen } = useModal();
  const userRole = localStorage.getItem('userRole');

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      const startDate = format(dateRange.startDate, "yyyy-MM-dd");
      const endDate = format(dateRange.endDate, "yyyy-MM-dd");
      dispatch(getAllCampaigns({ page, rowsPerPage, startDate, endDate }));
    } else {
      dispatch(getAllCampaigns({ page, rowsPerPage }));
    }
  }, [dateRange, dispatch, page, rowsPerPage]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm) => {
        dispatch(getAllCampaigns({ page: 0, rowsPerPage, name: searchTerm }));
      }, 2000),
    [dispatch, rowsPerPage]
  );

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  const triggerSearch = () => {
    dispatch(getAllCampaigns({ page: 0, rowsPerPage, name: search }));
  };

  const handleFilterChange = (value) => {
    let boolValue;
    if (value === "ACTIVATED") {
      boolValue = true;
    } else if (value === "DEACTIVATED") {
      boolValue = false;
    } else {
      boolValue = null;
    }
    dispatch(getAllCampaigns({ page: 0, rowsPerPage, status: boolValue }));
  };

  // const handleApproveClick = (id) => {
  //   dispatch(approveRejectCampaign({ id, state: "APPROVED" }))
  //   .unwrap()
  //   .then(() => {
  //     dispatch(getAllCampaigns({ page, rowsPerPage }));
  //   })
  //   .catch((error) => {
  //     console.error("Failed to update advertiser status:", error);
  //   });
  // };
  // const handleRejectClick = (id) => {
  //   dispatch(approveRejectCampaign({ id, state: "REJECTED" }))
  //   .unwrap()
  //   .then(() => {
  //     dispatch(getAllCampaigns({ page, rowsPerPage }));
  //   })
  //   .catch((error) => {
  //     console.error("Failed to update advertiser status:", error);
  //   });
  // };

  const handleApproveClick = (id) => {
    Swal.fire({
      title: "Approve Campaign",
      html: `
        <label for="publisherCPA" style="display:block; margin-bottom:5px;">Publisher CPA</label>
        <input id="publisherCPA" type="text" class="swal2-input" placeholder="Enter Publisher CPA">
        <label for="campaignCPA" style="display:block; margin-top:15px; margin-bottom:5px;">Campaign CPA</label>
        <input id="campaignCPA" type="text" class="swal2-input" placeholder="Enter Campaign CPA">
      `,
      confirmButtonText: "Approve",
      showCancelButton: true,
      preConfirm: () => {
        const publisherCPA = document.getElementById("publisherCPA").value;
        const campaignCPA = document.getElementById("campaignCPA").value;

        if (!publisherCPA || !campaignCPA) {
          Swal.showValidationMessage("Both CPA values are required!");
          return;
        }

        return {
          state: "APPROVED",
          publisherCPA: publisherCPA,
          campaignCPA: campaignCPA,
          note: "",
        };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        //console.log(result)
        //return
        dispatch(approveRejectCampaign({ id, ...result.value }))
          .unwrap()
          .then(() => {
            dispatch(getAllCampaigns({ page, rowsPerPage }));
          })
          .catch((error) => {
            console.error("Failed to approve campaign:", error);
          });
      }
    });
  };

  const handleRejectClick = (id) => {
    Swal.fire({
      title: "Reject Campaign",
      html: `
        <label for="reason" style="display:block; margin-bottom:5px;">Reason</label>
        <textarea id="reason" class="swal2-textarea" placeholder="Enter reason for rejection"></textarea>
      `,
      confirmButtonText: "Reject",
      showCancelButton: true,
      preConfirm: () => {
        const note = document.getElementById("reason").value;

        if (!note) {
          Swal.showValidationMessage("Reason is required!");
          return;
        }

        return {
          state: "REJECTED",
          publisherCPA: "0",
          campaignCPA: "0",
          note,
        };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(approveRejectCampaign({ id, ...result.value }))
          .unwrap()
          .then(() => {
            dispatch(getAllCampaigns({ page, rowsPerPage }));
          })
          .catch((error) => {
            console.error("Failed to reject campaign:", error);
          });
      }
    });
  };

  const handlePause = (id) => {
    dispatch(statusCampaign(id))
      .unwrap()
      .then(() => {
        dispatch(getAllCampaigns({ page, rowsPerPage }));
      })
      .catch((error) => {
        console.error("Failed to reject campaign:", error);
      });
  };

  const updateCpa = async (id) => {
    await dispatch(getCpaByCampaignId(id)).then((action) => {
      if (action.payload?.statusCode === 200) {
        const { costPerUserCampaign, costPerUserPublisher } =
          action.payload.data;

        Swal.fire({
          title: "Update CPA",
          html: `
            <label for="publisherCPA" style="display:block; margin-bottom:5px;">Publisher CPA</label>
            <input id="publisherCPA" type="text" class="swal2-input" value="${costPerUserPublisher}" placeholder="Enter Publisher CPA">
            <label for="campaignCPA" style="display:block; margin-top:15px; margin-bottom:5px;">Campaign CPA</label>
            <input id="campaignCPA" type="text" class="swal2-input" value="${costPerUserCampaign}" placeholder="Enter Campaign CPA">
          `,
          confirmButtonText: "Update",
          showCancelButton: true,
          preConfirm: () => {
            const publisherCPA = document.getElementById("publisherCPA").value;
            const campaignCPA = document.getElementById("campaignCPA").value;

            return {
              costPerUserPublisher: parseFloat(publisherCPA),
              costPerUserCampaign: parseFloat(campaignCPA),
            };
          },
        }).then((result) => {
          if (result.isConfirmed) {
            dispatch(updateCampaignCpa({ id, ...result.value }))
              .unwrap()
              .then(() => {
                dispatch(getAllCampaigns({ page, rowsPerPage }));
                dispatch(getCpaByCampaignId(id));
                Swal.fire("Success", "CPA updated successfully!", "success");
              })
              .catch((error) => {
                console.error("Failed to update CPA:", error);
              });
          }
        });
      } else {
        console.error("Failed to fetch CPA data:", action.error);
        Swal.fire("Oops..", "Failed to fetch CPA data", "error");
      }
    });
  };

  //Postback settings
  const handleViewClick = (id) => {
    setSelectedId(id); // Store the selected ID
    showModal("postbackSetting"); // Open the modal
    //console.log(id);
    dispatch(getPostbackSettings(id));
  };
  const [formData, setFormData] = useState({
    timeInterval: 0,
    range: 0,
    pbPublisher: 0,
    pbDigiClick: 0,
    staticId: parseInt(selectedId),
  });
  useEffect(() => {
    if (postbackSetting) {
      setFormData({
        timeInterval: parseInt(postbackSetting.timeInterval),
        range: parseInt(postbackSetting.range),
        pbPublisher: parseInt(postbackSetting.pbPublisher),
        pbDigiClick: parseInt(postbackSetting.pbDigiClick),
      });
    }
  }, [postbackSetting]);
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "pbPublisher") {
      const publisherValue = Math.min(100, parseInt(value));
      setFormData((prev) => ({
        ...prev,
        pbPublisher: publisherValue,
        pbDigiClick: 100 - publisherValue, // Automatically calculate pbDigiClick
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: parseInt(value) }));
    }
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    //console.log(formdata, selectedId)
    //return
    dispatch(createPostbackSettings({ id: selectedId, formData })).then(() =>
      hideModal("postbackSetting")
    );
    setFormData({
      timeInterval: 0,
      range: 0,
      pbPublisher: 0,
      pbDigiClick: 0,
      staticId: null,
    });
  };
  const handleModalClose = () => {
    setSelectedId(null);
    hideModal("postbackSetting"); // Close the modal
    setFormData({
      timeInterval: 0,
      range: 0,
      pbPublisher: 0,
      pbDigiClick: 0,
      staticId: null,
    }); // Reset form data
  };

  return (
    <Container>
      {loading ? (
        <div> </div>
      ) : (
        <GenericTableComponent
          columns={columns}
          data={campaigns}
          filterSearch={true}
          paginate={true}
          viewLink="/view-campaign"
          editLink="/edit-campaign"
          publisherLink="/publisher-link"
          page={page}
          rowsPerPage={rowsPerPage}
          handlePageChange={handlePageChange}
          triggerSearch={triggerSearch}
          search={search}
          handleRowsPerPageChange={handleRowsPerPageChange}
          handleSearchChange={handleSearchChange}
          totalItems={meta?.itemCount || 0}
          handleApproveClick={handleApproveClick}
          handleRejectClick={handleRejectClick}
          handlePause={handlePause}
          updateCpa={userRole === 'SUPPORT' ? false : updateCpa}
          hideEdit={userRole === 'SUPPORT' ? true : false}
          hideApprove={userRole === 'SUPPORT' ? true : false}
          hideReject={userRole === 'SUPPORT' ? true : false}
          setDateRange={setDateRange}
          dateRange={dateRange}
          onFilterChange={handleFilterChange}
          hideActiveSearch
          margined
          showModal={handleViewClick}
          campaignPostback
        />
      )}

      <Modal
        isOpen={isModalOpen("postbackSetting")}
        label="Postback Settings"
        handleClose={handleModalClose}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Postback Time Interval (mins)</label>
            <input
              type="number"
              name="timeInterval"
              value={formData.timeInterval}
              onChange={handleChange}
              min="0"
            />
          </div>

          <div className="form-group">
            <label>Minimum Postback</label>
            <input
              type="number"
              name="range"
              value={formData.range}
              onChange={handleChange}
              min="0"
            />
          </div>

          <div className="form-group">
            <label>Postback (Publisher) %</label>
            <input
              type="number"
              name="pbPublisher"
              value={formData.pbPublisher}
              onChange={handleChange}
              min="0"
              max="100"
            />
          </div>

          <div className="form-group">
            <label>Postback (ClickCircle) %</label>
            <input
              type="number"
              name="pbDigiClick"
              value={formData.pbDigiClick}
              onChange={handleChange}
              min="0"
              max="100"
              disabled
            />
          </div>

          <div className="submit">
            <div></div>
            <div className="submit-buttons">
              {/* <Link to='/campaigns'>
            <button className="cancel" type="submit" disabled={loading}>
              Pay Later
            </button></Link> */}
              <button className="add-campaign" type="submit" disabled={loading}>
                {loading ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </div>

          {/* <button type="submit" disabled={loading}>
            {loading ? "Saving..." : "Save Settings"}
          </button> */}
        </form>
      </Modal>
    </Container>
  );
};

export const Container = styled.div`
  @media screen and (max-width: 568px) {
    .header {
      flex-direction: column;
      gap: 20px;
      h3 {
        font-size: 15px;
        color: #2b2f38;
      }
    }
  }
  .header {
    margin-bottom: 30px;
    display: flex;
    justify-content: space-between;
    .texts {
      h3 {
        font-size: 32px;
        line-height: 44px;
        color: #2b2f38;
      }
      p {
        margin-top: 5px;
        line-height: 22px;
        font-size: 16px;
        color: #667085;
      }
    }

    .link-buttons {
      display: flex;
      gap: 10px;
      .see-more,
      .add-campaign {
        padding: 10px 16px;
        height: 40px;
        border: 1px solid #d0d5dd;
        border-radius: 8px;
        font-size: 14px;
        background-color: transparent;
        text-decoration: none;
        cursor: pointer;
        color: #344054;
      }
      .add-campaign {
        color: #fff;
        border: 1px solid #3730a3;
        background-color: #3730a3;
      }
    }
  }

  .form-group {
    margin-bottom: 1rem;

    label {
      margin-top: 20px;
      color: #344054;
      font-size: 14px;
    }

    input {
      width: 100%;
      //height: 43px;
      margin-top: 10px;
      border: solid 1px #e0e2e6;
      border-radius: 10px;
      padding: 12px 14px;
      transition: all 0.3s ease;

      &:focus {
        outline: #4f46e5;
        border: 1px solid #3730a3;
        background: transparent;
      }
    }

    /* label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: bold;
    }

    input {
      width: 100%;
      padding: 0.5rem;
      border: 1px solid #ccc;
      border-radius: 4px;
    } */
  }

  .submit {
    margin-top: 30px;
    display: flex;
    justify-content: space-between;
    .submit-buttons {
      display: flex;
      gap: 10px;
      .cancel,
      .add-campaign {
        padding: 10px 16px;
        height: 40px;
        border: 1px solid #d0d5dd;
        border-radius: 8px;
        font-size: 14px;
        background-color: transparent;
        text-decoration: none;
        cursor: pointer;
        color: #344054;
      }
      .add-campaign {
        color: #fff;
        border: 1px solid #3730a3;
        background-color: #3730a3;
      }
    }
  }
`;

export default AllCampaign;
